import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// Define types
interface Event {
  id: number;
  name: string;
  location: string;
  performance_time: string;
  end_time?: string;
  livestream?: boolean | number;
  currently_performing?: boolean;
}

interface RatingCount {
  rating: string;
  count: number;
}

interface HistoryPoint {
  time_interval: string;
  rating: string;
  count: number;
}

interface EventStats {
  likePct: number;
  neuPct: number;
  disPct: number;
}

// API URL
const API_URL = process.env.REACT_APP_API_URL || 'https://events.b2b2b.net';

// Styling
const neonStyle = {
  color: '#a9fefd',
  textShadow: [
    '0 0 2px #a9fefd',
    '0 0 6px #00d8e9',
    '0 0 10px #00b3c6'
  ].join(', ')
};

const redNeon = {
  color: '#ff6b6b',
  textShadow: '0 0 4px #ff6b6b, 0 0 10px #ff3b3b, 0 0 18px #c40000'
};

const purpleNeon = {
  color: '#bf9aff',
  textShadow: '0 0 4px #bf9aff, 0 0 10px #7f3afd, 0 0 18px #6411f2'
};

const VOTE_COOLDOWN_MS = 1_000;

// Rate Buttons Component
function RateButtons({ onVote }: { onVote: (rating: string) => void }) {
  const [lastVoteAt, setLastVoteAt] = useState(() => Number(localStorage.getItem('lastVoteAt')) || 0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const rem = Math.max(0, VOTE_COOLDOWN_MS - (now - lastVoteAt));
      setCooldownRemaining(rem);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [lastVoteAt]);

  const handleVote = (rating: string) => {
    const now = Date.now();
    if (now - lastVoteAt < VOTE_COOLDOWN_MS) return;
    onVote(rating);
    setLastVoteAt(now);
    localStorage.setItem('lastVoteAt', String(now));
  };

  const disabled = cooldownRemaining > 0;
  const secondsLeft = Math.ceil(cooldownRemaining / 1000);

  return (
    <div className="flex items-center gap-4">
      <button onClick={() => handleVote('like')} disabled={disabled} className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-50 px-4 py-2 rounded">🔥 FYAH</button>
      <button onClick={() => handleVote('neutral')} disabled={disabled} className="flex-1 bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50 px-4 py-2 rounded">😐 NEUTRAL</button>
      <button onClick={() => handleVote('dislike')} disabled={disabled} className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-50 px-4 py-2 rounded">NOT SO BUENO</button>
      {disabled && <span className="ml-4 text-sm text-gray-300">Next vote in {secondsLeft}s</span>}
    </div>
  );
}

// Helper function to get emoji based on stats
const getPulseEmoji = (stats: EventStats | undefined) => {
  if (!stats) return '';
  const { likePct, neuPct, disPct } = stats;
  if (likePct >= neuPct && likePct >= disPct) return '🔥';
  if (disPct >= likePct && disPct >= neuPct) return '😭';
  return '😐';
};

// Format time diff for "up next" feature
const formatTimeDiff = (diffMs: number) => {
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
  return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
};

// Main App Component
const App = () => {
  // Get local time in PST/PDT
  const getLocalTime = () => {
    const now = new Date();
    const pstIso = now.toLocaleString('sv-SE', { timeZone: 'America/Los_Angeles' }).replace(' ', 'T') + '-07:00';
    return new Date(pstIso);
  };

  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [ratings, setRatings] = useState<RatingCount[]>([]);
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const [eventRatingsMap, setEventRatingsMap] = useState<Record<number, EventStats>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(getLocalTime());
  const [selectedDay, setSelectedDay] = useState('2026-04-11');
  const [selectedStage, setSelectedStage] = useState('all');
  const [showLate, setShowLate] = useState(false);

  // Update current time every minute
  useEffect(() => {
    const iv = setInterval(() => setCurrentTime(getLocalTime()), 60_000);
    return () => clearInterval(iv);
  }, []);

  // Fetch events
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/api/events`);
        if (!res.ok) throw new Error();
        setEvents(await res.json());
      } catch {
        setError('Failed to load events');
      } finally {
        setLoading(false);
      }
    };
    load();
    const iv = setInterval(load, 30_000);
    return () => clearInterval(iv);
  }, []);

  // Get currently playing events
  const currentlyPlaying = useMemo(() => {
    const now = currentTime.getTime();
    const playing = events.filter(ev => {
      const startMs = new Date(ev.performance_time.replace(' ', 'T') + '-07:00').getTime();
      const endMs = ev.end_time 
        ? new Date(ev.end_time.replace(' ', 'T') + '-07:00').getTime() 
        : startMs + 90 * 60 * 1000;
      return now >= startMs && now < endMs;
    });
    if (!selectedEvent && playing.length) setSelectedEvent(playing[0]);
    return playing;
  }, [events, currentTime, selectedEvent]);

  // Get upcoming events for the day
  const upcomingEvents = useMemo(() => {
    if (currentlyPlaying.length > 0) return [];
    
    const now = currentTime.getTime();
    return events
      .filter(ev => {
        // Only include events for the current day
        const eventDate = ev.performance_time.substring(0, 10);
        const currentDate = currentTime.toISOString().substring(0, 10);
        if (eventDate !== currentDate) return false;
        
        // Only include future events
        const startMs = new Date(ev.performance_time.replace(' ', 'T') + '-07:00').getTime();
        return startMs > now;
      })
      .sort((a, b) => {
        const startA = new Date(a.performance_time.replace(' ', 'T') + '-07:00').getTime();
        const startB = new Date(b.performance_time.replace(' ', 'T') + '-07:00').getTime();
        return startA - startB;
      });
  }, [events, currentTime, currentlyPlaying]);

  // Get the very next event
  const nextEvent = useMemo(() => {
    return upcomingEvents.length > 0 ? upcomingEvents[0] : null;
  }, [upcomingEvents]);

  // Calculate time until next event
  const timeUntilNext = useMemo(() => {
    if (!nextEvent) return null;
    
    const now = currentTime.getTime();
    const nextStart = new Date(nextEvent.performance_time.replace(' ', 'T') + '-07:00').getTime();
    const diffMs = nextStart - now;
    
    return formatTimeDiff(diffMs);
  }, [nextEvent, currentTime]);

  // Fetch ratings for currently playing events
  useEffect(() => {
    currentlyPlaying.forEach(ev => {
      fetch(`${API_URL}/api/events/${ev.id}/ratings`)
        .then(r => r.json())
        .then((data: RatingCount[]) => {
          const total = data.reduce((s, r) => s + r.count, 0) || 1;
          const like = data.find(r => r.rating === 'like')?.count || 0;
          const neu = data.find(r => r.rating === 'neutral')?.count || 0;
          const dis = data.find(r => r.rating === 'dislike')?.count || 0;
          setEventRatingsMap(prev => ({
            ...prev,
            [ev.id]: {
              likePct: Math.round(like / total * 100),
              neuPct: Math.round(neu / total * 100),
              disPct: Math.round(dis / total * 100)
            }
          }));
        })
        .catch(() => {});
    });
  }, [currentlyPlaying]);

  // Fetch detailed ratings and history for selected event
  useEffect(() => {
    if (!selectedEvent) return;
    const loadDetail = async () => {
      try {
        const [rRes, hRes] = await Promise.all([
          fetch(`${API_URL}/api/events/${selectedEvent.id}/ratings`),
          fetch(`${API_URL}/api/events/${selectedEvent.id}/history`)
        ]);
        if (!rRes.ok || !hRes.ok) throw new Error();
        setRatings(await rRes.json());
        setHistory(await hRes.json());
      } catch {
        setError('Failed to load details');
      }
    };
    loadDetail();
    const iv = setInterval(loadDetail, 10_000);
    return () => clearInterval(iv);
  }, [selectedEvent]);

  // Submit a rating
  const submitRating = async (rating: string) => {
    if (!selectedEvent) return;
    try {
      await fetch(`${API_URL}/api/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventId: selectedEvent.id, rating })
      });
      const rRes = await fetch(`${API_URL}/api/events/${selectedEvent.id}/ratings`);
      if (rRes.ok) setRatings(await rRes.json());
    } catch {
      setError('Failed to submit');
    }
  };

  // Calculate stats for selected event
  const total = ratings.reduce((s, r) => s + r.count, 0) || 1;
  const stats = {
    like: ratings.find(r => r.rating === 'like')?.count || 0,
    neutral: ratings.find(r => r.rating === 'neutral')?.count || 0,
    dislike: ratings.find(r => r.rating === 'dislike')?.count || 0,
    likePct: Math.round((ratings.find(r => r.rating === 'like')?.count || 0) / total * 100),
    neuPct: Math.round((ratings.find(r => r.rating === 'neutral')?.count || 0) / total * 100),
    disPct: Math.round((ratings.find(r => r.rating === 'dislike')?.count || 0) / total * 100),
    total
  };

  // Format data for chart
  const chartData = Object.values(history.reduce((acc: Record<string, any>, p) => {
    if (!acc[p.time_interval]) acc[p.time_interval] = { time: p.time_interval, like: 0, neutral: 0, dislike: 0 };
    acc[p.time_interval][p.rating] = p.count;
    return acc;
  }, {}));

  // Get unique stages
  const uniqueStages = useMemo(() => Array.from(new Set(events.map(e => e.location))).sort(), [events]);

  // Filter events
 const filteredEvents = useMemo(() => {
  return events
    .filter(e => e.performance_time.startsWith(selectedDay))
    .filter(e => selectedStage === 'all' || e.location === selectedStage)
    .filter(e => {
      if (!showLate) return true; // If not showing only late night sets, include all
      
      // Only include performances starting at or after 10pm
      const startTime = new Date(e.performance_time.replace(' ', 'T') + '-07:00');
      return startTime.getHours() >= 22 || startTime.getHours() < 4; // Including shows from 10pm to 4am
    })
    .sort((a, b) => {
      const timeA = new Date(a.performance_time.replace(' ', 'T') + '-07:00').getTime();
      const timeB = new Date(b.performance_time.replace(' ', 'T') + '-07:00').getTime();
      return timeA - timeB;
    });
}, [events, selectedDay, selectedStage, showLate]);


  if (loading) return <div className="flex justify-center items-center h-screen">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-800 text-white p-4">
      <h1 className="text-5xl font-bold text-center mb-4" style={neonStyle}>✨ Coachella 2026 Live Ratings 🎵</h1>
      <p className="text-center text-cyan-400 mb-8 text-xl">
        🕒 Current Coachella Time: {currentTime.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit', hour12: true })}
      </p>
      {error && <div className="bg-red-900 px-4 py-2 mb-4 rounded">{error}</div>}

      {currentlyPlaying.length > 0 ? (
        <section className="mb-8">
          <h2 className="text-3xl font-bold text-center mb-4" style={redNeon}>🎤 HAPPENING NOW 🎸</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentlyPlaying.map(event => (
              <div 
                key={event.id}
                className={`p-4 rounded border-2 cursor-pointer transition-all hover:shadow-lg ${
                  selectedEvent?.id === event.id 
                    ? 'border-cyan-400 bg-slate-700' 
                    : 'border-slate-600 bg-slate-900 hover:border-slate-400'
                }`}
                onClick={() => setSelectedEvent(event)}
              >
                <div className="flex justify-between">
                  <h3 className="text-xl font-bold">{event.name}</h3>
                  {eventRatingsMap[event.id] && (
                    <span className="text-xl">{getPulseEmoji(eventRatingsMap[event.id])}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-gray-300">{event.location}</p>
                  {event.livestream ? <span className="text-xs bg-red-600 px-1.5 py-0.5 rounded-full font-medium">LIVE</span> : null}
                </div>
                <p className="text-sm text-gray-400">
                  Started: {new Date(event.performance_time.replace(' ', 'T') + '-04:00').toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </p>
                
                {eventRatingsMap[event.id] && (
                  <div className="mt-2">
                    <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div className="flex h-full">
                        <div 
                          className="bg-green-500" 
                          style={{ width: `${eventRatingsMap[event.id].likePct}%` }}
                        ></div>
                        <div 
                          className="bg-yellow-500" 
                          style={{ width: `${eventRatingsMap[event.id].neuPct}%` }}
                        ></div>
                        <div 
                          className="bg-red-500" 
                          style={{ width: `${eventRatingsMap[event.id].disPct}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      ) : nextEvent ? (
        <section className="mb-8">
          <h2 className="text-3xl font-bold text-center mb-4" style={purpleNeon}>🔜 UP NEXT 🔜</h2>
          <div className="max-w-2xl mx-auto bg-slate-700 p-6 rounded-lg border-2 border-purple-300">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-2xl font-bold">{nextEvent.name}</h3>
                <p className="text-gray-300">{nextEvent.location}</p>
                <p className="text-gray-400 mt-2">
                  Starting at: {new Date(nextEvent.performance_time.replace(' ', 'T') + '-07:00').toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </p>
              </div>
              <div className="bg-purple-900 px-4 py-2 rounded-lg">
                <p className="text-purple-200 text-sm font-medium">Starting in:</p>
                <p className="text-white text-xl font-bold">{timeUntilNext}</p>
              </div>
            </div>
            
            <p className="mt-4 text-center text-gray-300">
              Get ready to rate this performance when it starts!
            </p>
          </div>
        </section>
      ) : currentTime.getHours() >= 1 && (
        <div className="text-center text-2xl mt-12" style={redNeon}>
          🎉 That's it for tonight — come back tomorrow at 4PM PT / 7PM ET 🎶
        </div>
      )}

      {selectedEvent && (
        <section className="mb-8 max-w-4xl mx-auto">
          <div className="bg-slate-700 p-6 rounded-lg border border-cyan-800">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-2xl font-bold">{selectedEvent.name}</h2>
                <p className="text-gray-300">{selectedEvent.location}</p>
                <p className="text-sm text-gray-400">
                  {new Date(selectedEvent.performance_time.replace(' ', 'T') + '-07:00').toLocaleString([], {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </p>
              </div>
            </div>
            
            <h3 className="text-xl font-semibold mb-3">Rate this performance:</h3>
            <RateButtons onVote={submitRating} />
            
            <div className="mt-6">
              <h3 className="text-xl font-semibold mb-3">Current Ratings:</h3>
              <div className="flex justify-between items-center mb-3">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-500">{stats.likePct}%</div>
                  <div className="text-sm text-gray-400">🔥 FYAH ({stats.like})</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-500">{stats.neuPct}%</div>
                  <div className="text-sm text-gray-400">😐 NEUTRAL ({stats.neutral})</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-500">{stats.disPct}%</div>
               <div
  className="text-sm text-gray-400"
  style={{
    fontSize: window.innerWidth < 640 ? '60%' : '70%'
  }}
>
 <span className="text-gray-400" style={{ fontSize: '0.7em' }}>
  NO BUENO({stats.dislike})
</span>
</div>
                </div>
              </div>
              
              <div className="w-full h-4 bg-gray-700 rounded-full overflow-hidden">
                <div className="flex h-full">
                  <div 
                    className="bg-green-500" 
                    style={{ width: `${stats.likePct}%` }}
                  ></div>
                  <div 
                    className="bg-yellow-500" 
                    style={{ width: `${stats.neuPct}%` }}
                  ></div>
                  <div 
                    className="bg-red-500" 
                    style={{ width: `${stats.disPct}%` }}
                  ></div>
                </div>
              </div>
              <div className="mt-1 text-right text-sm text-gray-400">
                Total ratings: {stats.total}
              </div>
            </div>
            
            {chartData.length > 0 && (
              <div className="mt-6">
                <h3 className="text-xl font-semibold mb-3">Rating History:</h3>
                <div className="h-64 bg-slate-800 p-2 rounded">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                    <XAxis
  dataKey="time_interval"
  tickFormatter={(timeStr) => {
    if (!timeStr || timeStr === 'undefined') return '';
    const date = new Date(timeStr + 'Z'); // treat input as UTC
    return date.toLocaleTimeString('en-US', {
      timeZone: 'America/Los_Angeles',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false, // 24-hour format
    });
  }}
  stroke="#aaa"
/>
                      <YAxis stroke="#aaa" />
                      <Tooltip 
                        formatter={(value: number, name: string) => {
                          const nameStr = String(name);
                          return [value, nameStr.charAt(0).toUpperCase() + nameStr.slice(1)];
                        }}
                        labelFormatter={(time: string) => `Time: ${String(time).split(' ')[1]}`}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="like" 
                        stroke="#10B981" 
                        strokeWidth={2} 
                        name="FYAH" 
                        dot={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="neutral" 
                        stroke="#F59E0B" 
                        strokeWidth={2} 
                        name="Neutral" 
                        dot={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="dislike" 
                        stroke="#EF4444" 
                        strokeWidth={2} 
                        name="No so bueno" 
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </section>
      )}
      
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">All Performances</h2>
        
        {/* Day selection tabs - UPDATED WITH BETTER HIGHLIGHTING */}
        <div className="flex gap-2 mb-4">
          <button 
            className={`px-4 py-2 rounded-t-lg ${
              selectedDay === '2026-04-10' 
                ? 'bg-cyan-600 text-white font-bold' 
                : 'bg-slate-600 text-gray-200'
            }`}
            onClick={() => setSelectedDay('2026-04-10')}
          >
            Friday
          </button>
          <button 
            className={`px-4 py-2 rounded-t-lg ${
              selectedDay === '2026-04-11' 
                ? 'bg-cyan-600 text-white font-bold' 
                : 'bg-slate-600 text-gray-200'
            }`}
            onClick={() => setSelectedDay('2026-04-11')}
          >
            Saturday
          </button>
          <button 
            className={`px-4 py-2 rounded-t-lg ${
              selectedDay === '2026-04-12' 
                ? 'bg-cyan-600 text-white font-bold' 
                : 'bg-slate-600 text-gray-200'
            }`}
            onClick={() => setSelectedDay('2026-04-12')}
          >
            Sunday
          </button>
        </div>
        
        {/* Stage selection buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            className={`px-3 py-1 text-sm rounded-full ${selectedStage === 'all' ? 'bg-cyan-600 font-medium' : 'bg-slate-600'}`}
            onClick={() => setSelectedStage('all')}
          >
            All Stages
          </button>
          {uniqueStages.map(stage => (
            <button
              key={stage}
              className={`px-3 py-1 text-sm rounded-full ${selectedStage === stage ? 'bg-cyan-600 font-medium' : 'bg-slate-600'}`}
              onClick={() => setSelectedStage(stage)}
            >
              {stage}
            </button>
          ))}
        </div>
        
        {/* Late night filter checkbox */}
        <div className="mb-4">
          <label className="flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              checked={showLate} 
              onChange={e => setShowLate(e.target.checked)} 
              className="mr-2 h-4 w-4"
            />
            <span>Show only late night sets (after 10pm)</span>
          </label>
        </div>
        
        {/* Event grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredEvents.map(event => {
            const startTime = new Date(event.performance_time.replace(' ', 'T') + '-07:00');
            const isPlaying = currentlyPlaying.some(e => e.id === event.id);
            const isUpcoming = startTime.getTime() > currentTime.getTime();
            
            return (
              <div 
                key={event.id}
                className={`p-3 rounded border cursor-pointer ${
                  isPlaying ? 'bg-slate-700 border-green-500' :
                  isUpcoming ? 'bg-slate-800 border-slate-600' :
                  'bg-slate-900 border-slate-700 opacity-60'
                }`}
                onClick={() => setSelectedEvent(event)}
              >
                <div className="flex justify-between">
                  <h3 className="font-semibold">{event.name}</h3>
                  {isPlaying && <span className="text-green-400 text-sm">Live now</span>}
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-gray-400">{event.location}</p>
                  {event.livestream ? <span className="text-xs bg-red-600 px-1.5 py-0.5 rounded-full font-medium">LIVE</span> : null}
                </div>
                <p className="text-xs text-gray-500">
                  {startTime.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </p>
              </div>
            );
          })}
        </div>
        
        {filteredEvents.length === 0 && (
          <div className="text-center p-8 bg-slate-900 rounded">
            No performances found matching your filters.
          </div>
        )}
      </section>
      
      <footer className="text-center text-gray-500 text-sm mt-8">
        © 2026 Coachella Festival - Real-time Rating System
      </footer>
    </div>
  );
};

export default App;